/**
 * SQL Formatter Service
 * Implements business logic for SQL formatting and minification
 */

import { format as formatSql } from 'sql-formatter';
import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type SqlDialect = 'sql' | 'mysql' | 'postgresql' | 'sqlite';
export type KeywordCase = 'upper' | 'lower' | 'preserve';

export class SqlFormatterService {
  /**
   * Format SQL with pretty printing
   */
  static async format(
    sql: string,
    options: {
      dialect?: SqlDialect;
      indentSize?: number;
      keywordCase?: KeywordCase;
    } = {}
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(sql,
        CommonValidator.getValidationRules(EncoderToolType.SQL_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.SQL_FORMATTER, validation.suggestion);
      }

      // Format SQL using sql-formatter
      const dialect = options.dialect || 'sql';
      const indentSize = options.indentSize || 2;
      const keywordCase = options.keywordCase || 'upper';

      const formatted = formatSql(sql, {
        language: dialect,
        tabWidth: indentSize,
        keywordCase: keywordCase === 'preserve' ? undefined : keywordCase,
        linesBetweenQueries: 2
      });

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.SQL_FORMATTER,
        operationType: 'encode',
        inputData: sql,
        outputData: formatted,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'format',
          dialect,
          indentSize,
          keywordCase,
          originalLength: sql.length,
          formattedLength: formatted.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to format SQL',
        EncoderToolType.SQL_FORMATTER,
        'Please check your SQL syntax'
      );
    }
  }

  /**
   * Minify SQL (remove unnecessary whitespace)
   */
  static async minify(sql: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(sql,
        CommonValidator.getValidationRules(EncoderToolType.SQL_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.SQL_FORMATTER, validation.suggestion);
      }

      // Simple minification: remove extra whitespace
      const minified = sql
        .replace(/\s+/g, ' ')
        .replace(/\s*([(),;])\s*/g, '$1')
        .trim();

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.SQL_FORMATTER,
        operationType: 'encode',
        inputData: sql,
        outputData: minified,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'minify',
          originalLength: sql.length,
          minifiedLength: minified.length,
          compressionRatio: ((1 - minified.length / sql.length) * 100).toFixed(2) + '%'
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to minify SQL',
        EncoderToolType.SQL_FORMATTER,
        'An unexpected error occurred'
      );
    }
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `sql-formatter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
